import React from 'react';
import {
  Card,
  Icon,
  Button,
  Field,
  Heading,
  Input,
  Label,
  RichTextEditor,
  ValidationMessage,
} from '@innovationnorway/cl-react';
import { Controller } from 'react-hook-form';
import LabelWithTag from './LabelWithTag';
import useAboutForm, { AboutData } from '../hooks/subforms/use-about-form';
import { SubFormProps } from '../hooks/use-service-sub-form';
import { useTranslation } from 'react-i18next';
import { MutableRefObject } from 'react';
import { PURPOSE_MAX_LENGTH } from '../constants/service-form-max-lengths';

type AboutPageProps = SubFormProps<AboutData> & {
  purposeRawTextRef: MutableRefObject<string>;
};

const AboutPage = ({ purposeRawTextRef, ...props }: AboutPageProps) => {
  const { form, registerWithRules, rules } = useAboutForm({
    ...props,
    purposeRawTextRef,
  });
  const { formState, getValues, setValue, control } = form;
  const { errors } = props;
  const { t: tServices } = useTranslation('services');
  const { t: tCommon } = useTranslation('common');

  const [newUrl, setNewUrl] = React.useState<string>('');
  const [newUrlText, setNewUrlText] = React.useState<string>('');

  const [keywords, setKeywords] = React.useState<string[]>(getValues('keywords') || []);
  const [newKeyword, setNewKeyword] = React.useState<string>('');

  return (
    <div>
      <Heading level={1}>{tServices('about.title')}</Heading>
      <div className="display--flex flex-direction--column gap--5 margin-top--2">
        <div>
          <Field>
            <Label>{tServices('about.serviceName.label')}</Label>
            <Field.Description data-size="sm">{tServices('about.serviceName.help')}</Field.Description>
            <Input {...registerWithRules('name')} />
            {(errors.name?.message || formState.errors.name?.message) && (
              <ValidationMessage>{errors.name?.message || formState.errors.name?.message}</ValidationMessage>
            )}
          </Field>
        </div>
        <div className="display--flex gap--5">
          <Field>
            <Label>{tServices('about.validFrom.label')}</Label>
            <Input type="date" {...registerWithRules('validFrom')} />
            {(errors.validFrom?.message || formState.errors.validFrom?.message) && (
              <ValidationMessage>
                {errors.validFrom?.message || formState.errors.validFrom?.message}
              </ValidationMessage>
            )}
          </Field>
          <Field>
            <Label>{tServices('about.validTo.label')}</Label>
            <Input type="date" {...registerWithRules('validTo')} />
            {(errors.validTo?.message || formState.errors.validTo?.message) && (
              <ValidationMessage>
                {errors.validTo?.message || formState.errors.validTo?.message}
              </ValidationMessage>
            )}
          </Field>
        </div>
        <div>
          <Controller
            control={control}
            name="servicePurpose"
            rules={rules.servicePurpose}
            render={({ field, fieldState }) => {
              return (
                <Field>
                  <Label>{tServices('about.purpose.label')}</Label>
                  <Field.Description data-size="sm">{tServices('about.purpose.help')}</Field.Description>
                  <RichTextEditor
                    onChange={field.onChange}
                    onChangeRawText={(rawText) => {
                      purposeRawTextRef.current = rawText;
                    }}
                    onBlur={field.onBlur}
                    initialValue={field.value}
                    rows={6}
                    counter={PURPOSE_MAX_LENGTH}
                  />
                  {(fieldState.error?.message || errors.servicePurpose?.message) && (
                    <ValidationMessage>
                      {fieldState.error?.message || errors.servicePurpose?.message}
                    </ValidationMessage>
                  )}
                </Field>
              );
            }}
          />
        </div>
        <div>
          <Controller
            control={control}
            name="description"
            rules={rules.description}
            render={({ field, fieldState }) => {
              return (
                <Field>
                  <Label>{tServices('about.description.label')}</Label>
                  <Field.Description data-size="sm">{tServices('about.description.help')}</Field.Description>
                  <RichTextEditor
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    initialValue={field.value}
                    namespace="ServiceDescriptionEditor"
                    rows={6}
                  />
                  {fieldState.error && (
                    <ValidationMessage>
                      {errors.description?.message || fieldState.error?.message}
                    </ValidationMessage>
                  )}
                </Field>
              );
            }}
          />
        </div>
        <div>
          <Controller
            control={control}
            name="customerDescription"
            render={({ field, fieldState }) => {
              return (
                <Field>
                  <LabelWithTag data-color="accent" tagText={tCommon('optionalCapitalized')}>
                    {tServices('about.customerDescription.label')}
                  </LabelWithTag>
                  <Field.Description data-size="sm">
                    {tServices('about.customerDescription.help')}
                  </Field.Description>
                  <RichTextEditor
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    initialValue={field.value}
                    rows={6}
                    namespace="ServiceCustomerDescriptionEditor"
                  />
                  {fieldState.error && (
                    <ValidationMessage>
                      {errors.customerDescription?.message || fieldState.error?.message}
                    </ValidationMessage>
                  )}
                </Field>
              );
            }}
          />
        </div>
        <div>
          <Controller
            control={control}
            name="keywords"
            rules={rules.keywords}
            render={({ field, fieldState }) => (
              <>
                <div className="display--flex flex-direction--row gap--2 justify-content--space-between">
                  <Field>
                    <Label>{tServices('about.keywords.label')}</Label>
                    <Field.Description data-size="sm">{tServices('about.keywords.help')}</Field.Description>
                    <Input
                      maxLength={500}
                      value={newKeyword}
                      onChange={(e) => {
                        const nextValue = e.target.value;
                        setNewKeyword(nextValue);
                        if (fieldState.error?.message && nextValue.trim().length > 0) {
                          form.clearErrors('keywords');
                        }
                      }}
                      onBlur={field.onBlur}
                    />
                    {fieldState.error?.message && (
                      <ValidationMessage>{fieldState.error.message}</ValidationMessage>
                    )}
                  </Field>
                  <Button
                    className={`text-decoration__none align-self--flex-end${
                      fieldState.error?.message ? ' margin-bottom--4' : ''
                    }`}
                    icon
                    style={{ whiteSpace: 'nowrap', minWidth: 'fit-content' }}
                    variant="tertiary"
                    disabled={!newKeyword}
                    onClick={() => {
                      setKeywords([...keywords, newKeyword]);
                      setValue('keywords', [...keywords!, newKeyword]);
                      setNewKeyword('');
                      form.trigger('keywords');
                    }}
                    onBlur={field.onBlur}
                  >
                    <Icon prefix="fasr" iconName="plus" />
                    {tServices('form.add')}
                  </Button>
                </div>
                <div className="display--flex flex-direction--row flex-wrap--wrap gap--1 margin-top--2">
                  {getValues('keywords') !== undefined &&
                    getValues('keywords')?.map((keyword, index) => (
                      <span
                        key={index}
                        className="margin-right--1 padding-left--1 display--flex flex-direction--row align-items--center background-color--blue-50 border-radius--1"
                      >
                        {keyword}
                        <Button
                          type="button"
                          icon
                          variant="tertiary"
                          className="margin-left--1 font-size--large"
                          aria-label={`${tCommon('remove')} ${keyword}`}
                          onClick={() => {
                            const filteredKeywords = keywords.filter((_, i) => i !== index);
                            setKeywords(filteredKeywords);
                            setValue('keywords', filteredKeywords);
                            form.trigger('keywords');
                          }}
                        >
                          <Icon prefix="fasr" iconName="times" />
                        </Button>
                      </span>
                    ))}
                </div>
              </>
            )}
          />
        </div>
        <div>
          <Field>
            <LabelWithTag tagText={tCommon('optionalCapitalized')} data-color="accent">
              {tServices('about.links.title')}
            </LabelWithTag>
          </Field>
          <div className="display--flex justify-content--space-between gap--2">
            <div className="display--flex gap--1 flex-direction--row flex-grow--1">
              <Field className="width--30">
                <Field.Description>{tServices('about.links.text')}</Field.Description>
                <Input onChange={(e) => setNewUrlText(e.target.value)} />
              </Field>
              <Field className="width--70">
                <Field.Description>{tServices('about.links.url')}</Field.Description>
                <Input onChange={(e) => setNewUrl(e.target.value)} />
              </Field>
            </div>
            <Button
              variant="tertiary"
              icon
              className="align-self--flex-end text-decoration__none min-width--fit-content"
              disabled={!newUrl || !newUrlText}
              onClick={() => {
                setValue('externalLinks', [
                  ...getValues('externalLinks'),
                  { text: newUrlText, url: !/^https?:\/\//i.test(newUrl) ? `https://${newUrl}` : newUrl },
                ]);
                setNewUrl('');
                setNewUrlText('');
              }}
            >
              <Icon prefix="fasr" iconName="plus" />
              {tServices('form.add')}
            </Button>
          </div>
          {getValues('externalLinks').length > 0 &&
            getValues('externalLinks').map((url, index) => {
              if (!url || !url.url || !url.text) return null;
              return (
                <Card key={index} className="margin-top--2">
                  <div className="display--flex align-items--center">
                    <a href={url.url} target="_blank" rel="noopener noreferrer">
                      {url.text}
                    </a>
                    <Button
                      type="button"
                      icon
                      variant="tertiary"
                      className="margin-left--auto font-size--large"
                      aria-label={`${tCommon('remove')} ${url.text}`}
                      onClick={() => {
                        setValue(
                          'externalLinks',
                          getValues('externalLinks').filter((_, i) => i !== index),
                        );
                      }}
                    >
                      <Icon prefix="fasr" iconName="times" />
                    </Button>
                  </div>
                </Card>
              );
            })}
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
